import type { StatusTone } from '@noor/ui';
import type { ModerationOperation } from './request-kind';
import { firstNonEmpty, type Translate } from './translate';

/** `PROMO_TYPE_DISCOUNT` / `discount` → `Discount`. */
export function prettifyEnumLabel(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '') return '—';
  const last = trimmed.includes('_')
    ? trimmed.slice(trimmed.lastIndexOf('_') + 1)
    : trimmed;
  const lower = last.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** `MEASUREMENT_TYPE_KG` → `kg`, `_GR` → `g`, `_ML` → `ml`, `_L` → `l`. */
export function measurementLabel(value: string | null | undefined): string {
  const normalized = (value ?? '').toUpperCase();
  if (normalized.endsWith('_KG')) return 'kg';
  if (normalized.endsWith('_GR')) return 'g';
  if (normalized.endsWith('_ML')) return 'ml';
  if (normalized.endsWith('_L')) return 'l';
  return prettifyEnumLabel(value);
}

export function operationLabel(op: string | null | undefined, t: Translate): string {
  switch ((op ?? '').toLowerCase()) {
    case 'create':
      return t('operation.create', 'Create');
    case 'update':
      return t('operation.update', 'Update');
    case 'delete':
      return t('operation.delete', 'Delete');
    case 'publish':
      return t('operation.publish', 'Publish');
    case 'transition':
      return t('operation.transition', 'Publish');
    default:
      return firstNonEmpty(op?.trim()) ?? '—';
  }
}

export function operationTone(op: string | null | undefined): StatusTone {
  switch ((op ?? '').toLowerCase()) {
    case 'create':
      return 'success';
    case 'delete':
      return 'danger';
    case 'publish':
    case 'transition':
      return 'active';
    default:
      return 'pending';
  }
}

/** Label of a classified operation (`requestOperation`) — the review UI's only one. */
export function operationKindLabel(kind: ModerationOperation, t: Translate): string {
  switch (kind) {
    case 'create':
      return t('operation.create', 'Создание');
    case 'update':
      return t('operation.update', 'Изменение');
    case 'delete':
      return t('operation.delete', 'Удаление');
  }
}

export function sourceLabel(source: string | null | undefined, t: Translate): string {
  switch ((source ?? '').toLowerCase()) {
    case 'manual':
      return t('source.manual', 'Manual');
    case 'sync':
      return t('source.sync', 'Sync');
    default:
      return firstNonEmpty(source?.trim()) ?? '—';
  }
}

export function statusTone(status: string | null | undefined): StatusTone {
  switch ((status ?? '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'deleted':
      return 'danger';
    case 'pending':
      return 'pending';
    default:
      return 'neutral';
  }
}

export function statusLabel(status: string | null | undefined, t: Translate): string {
  switch ((status ?? '').toLowerCase()) {
    case 'pending':
      return t('status.pending', 'На проверке');
    case 'approved':
      return t('status.approved', 'Одобрено');
    case 'rejected':
      return t('status.rejected', 'Отклонено');
    case 'stale':
      return t('status.stale', 'Устарело');
    case 'withdrawn':
      return t('status.withdrawn', 'Отозвано');
    case 'deleted':
      return t('status.deleted', 'Удалён');
    default:
      return firstNonEmpty(status?.trim()) ?? '—';
  }
}

const LIFE_STATUS = 'life';

/** Publication state shared by every moderated entity (`lifecycle_status`). */
export function lifecycleTone(value: string | null | undefined): StatusTone {
  const normalized = (value ?? '').toLowerCase();
  if (normalized.includes('terminated') || normalized.includes('deleted')) {
    return 'danger';
  }
  if (normalized.includes('ready')) return 'pending';
  if (normalized.includes('life')) return 'success';
  return 'neutral';
}

export function lifecycleLabel(value: string | null | undefined, t: Translate): string {
  const normalized = (value ?? '').toLowerCase();
  if (normalized.includes('terminated')) return t('lifecycle.terminated', 'Closed');
  if (normalized.includes('deleted')) return t('lifecycle.deleted', 'Deleted');
  if (normalized.includes('ready')) return t('lifecycle.ready', 'On review');
  if (normalized === LIFE_STATUS || normalized.endsWith('life')) {
    return t('lifecycle.life', 'Published');
  }
  if (normalized.includes('creat')) return t('lifecycle.created', 'Draft');
  return firstNonEmpty(value?.trim()) ?? '—';
}

/** Product availability (`PRODUCT_STATUS_AVAILABLE` / `STOP_LIST`). */
export function productStatusTone(value: string | null | undefined): StatusTone {
  const normalized = (value ?? '').toUpperCase();
  if (normalized.includes('STOP_LIST')) return 'danger';
  if (normalized.includes('AVAILABLE')) return 'success';
  return 'neutral';
}

export function productStatusLabel(
  value: string | null | undefined,
  t: Translate,
): string {
  const normalized = (value ?? '').toUpperCase();
  if (normalized.includes('STOP_LIST')) return t('productStatus.stopList', 'Stop list');
  if (normalized.includes('AVAILABLE')) {
    return t('productStatus.available', 'Available');
  }
  return firstNonEmpty(value?.trim()) ?? '—';
}
